<template>
  <div class="po-shell">
    <base-dialog :show="!!error" title="An error occurred!" @close="handleError">
      <p>{{ error }}</p>
    </base-dialog>
    <transition name="toast">
      <div
        v-if="baseToast.message"
        class="po-toast"
        :class="'is-' + (baseToast.type || 'info')"
      >
        <span class="dot"></span>
        <span>{{ baseToast.message }}</span>
      </div>
    </transition>

    <!-- ==================== Header ==================== -->
    <header class="po-header">
      <div>
        <div class="eyebrow">Operations</div>
        <h1>Pay Out</h1>
        <p class="subtitle">Live pay-out orders — pickups, approvals, and settlements across your branches.</p>
      </div>
      <div class="header-actions">
        <button
          v-if="isClient"
          class="btn btn-primary"
          type="button"
          @click.stop="$router.push('/payout/create')"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" class="btn-icon">
            <path d="M12 5v14M5 12h14" stroke-linecap="round" />
          </svg>
          Create Order
        </button>
        <button
          class="btn btn-secondary"
          type="button"
          :disabled="isExporting"
          @click.stop="exportData"
        >
          <svg v-if="!isExporting" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="btn-icon">
            <path d="M12 21V7M6 13l6 6 6-6M4 3h16" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <span v-else class="spinner-mini"></span>
          {{ isExporting ? 'Exporting…' : 'Export' }}
        </button>
        <button
          class="btn btn-icon-only"
          type="button"
          @click.stop="getPayOutList"
          title="Refresh"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </div>
    </header>

    <!-- ==================== View mode (bank/branch only) ==================== -->
    <section v-if="isUser || isSubAdmin" class="po-viewmode">
      <button
        type="button"
        class="vm-tab"
        :class="{ 'is-active': viewMode === 'my-orders' }"
        @click.stop="viewMode = 'my-orders'"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <circle cx="12" cy="8" r="4" />
          <path d="M4 21a8 8 0 0116 0" stroke-linecap="round" />
        </svg>
        My payout orders
      </button>
      <button
        type="button"
        class="vm-tab"
        :class="{ 'is-active': viewMode === 'available' }"
        @click.stop="viewMode = 'available'"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <path d="M12 2v20M2 12h20" stroke-linecap="round" />
          <circle cx="12" cy="12" r="10" />
        </svg>
        Available orders
      </button>
    </section>

    <!-- ==================== Filters bar ==================== -->
    <section class="po-filters">
      <div class="filters-row">
        <!-- Scoped search (10082026): the column selector and the input are
             ONE control — pick where to search, then type. -->
        <div class="search-wrap">
          <div class="search-scope">
            <select v-model.trim="searchField" aria-label="Search column">
              <option value="utr">UTR</option>
              <option value="receiptId">Receipt ID</option>
              <option value="merchantOrderId">Order ID</option>
              <option value="refID">Ref ID</option>
              <option value="customerName">Customer Name</option>
              <option value="validatorUPIID">UPI ID</option>
              <option value="clientName">Client</option>
              <option value="bank">Bank</option>
              <option value="all">All fields</option>
            </select>
            <svg class="scope-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
          <div class="search-box">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="search-icon">
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3.5-3.5" stroke-linecap="round" />
            </svg>
            <input
              v-model.trim="searchKeyWord"
              type="text"
              placeholder="Search by name, UTR, UPI, amount, order ID…"
              class="search-input search-input-joined"
            />
            <button
              v-if="searchKeyWord"
              type="button"
              class="search-clear"
              @click="searchKeyWord = ''"
              title="Clear"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
              </svg>
            </button>
          </div>
        </div>

        <div class="chip-group">
          <button
            v-for="s in visibleQuickStatuses"
            :key="s.value"
            type="button"
            class="chip"
            :class="[
              { 'chip-active': paymentStatus === s.value },
              paymentStatus === s.value && s.tone ? `chip-active-${s.tone}` : ''
            ]"
            @click.stop="paymentStatus = s.value"
          >{{ s.label }}</button>
        </div>

        <!-- Time window (10082026): every list query is range-bounded. Payout
             is exempt from the 31-day span cap (28102026) — archival keeps live
             orders to ~60 days, so wide ranges stay index-bounded (server matches). -->
        <div class="select-wrap period-select">
          <select :value="period" @change="setPeriod($event.target.value)" aria-label="Time period">
            <option v-for="p in periodChips" :key="p.value" :value="p.value">{{ p.label }}</option>
          </select>
          <svg class="select-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>


        <button
          type="button"
          class="btn btn-ghost"
          :class="{ 'is-active': showSearch }"
          @click.stop="toggleFilters"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="btn-icon">
            <path d="M3 6h18M6 12h12M10 18h4" stroke-linecap="round" />
          </svg>
          {{ showSearch ? 'Hide filters' : 'More filters' }}
        </button>
      </div>

      <!-- Advanced filters panel -->
      <transition name="collapse">
        <div v-if="showSearch" class="filters-advanced">
          <div class="advanced-grid">
            <div class="field field-range">
              <label>Amount range</label>
              <div class="range-inputs">
                <div class="input-wrap">
                  <span class="input-prefix">₹</span>
                  <input v-model.number="amountMin" type="number" min="0" placeholder="Min" />
                </div>
                <span class="range-sep">to</span>
                <div class="input-wrap">
                  <span class="input-prefix">₹</span>
                  <input v-model.number="amountMax" type="number" min="0" placeholder="Max" />
                </div>
              </div>
              <p v-if="amountRangeInvalid" class="field-error">Min cannot exceed Max.</p>
            </div>
            <div v-if="canFilterByBranch" class="field">
              <label>Branch</label>
              <div class="select-wrap">
                <select v-model.trim="validatorUsername">
                  <option value="">All branches</option>
                  <option v-for="b in branches" :key="b.username" :value="b.username">{{ b.name || b.username }}</option>
                </select>
                <svg class="select-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
            </div>
            <div v-if="canFilterByBranch" class="field">
              <label>Client</label>
              <div class="select-wrap">
                <select v-model.trim="clientName">
                  <option value="">All clients</option>
                  <option v-for="c in clients" :key="c.clientName" :value="c.clientName">{{ c.clientName }}</option>
                </select>
                <svg class="select-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
            </div>
            <div class="field">
              <label>UTR</label>
              <div class="select-wrap">
                <select v-model.trim="utrFilter">
                  <option value="">All orders</option>
                  <option value="with_utr">With UTR</option>
                  <option value="without_utr">Without UTR</option>
                </select>
                <svg class="select-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
            </div>
            <div class="field">
              <label>Mode</label>
              <div class="select-wrap">
                <select v-model.trim="paymentMode">
                  <option value="">All modes</option>
                  <option value="IMPS">IMPS</option>
                  <option value="NEFT">NEFT</option>
                  <option value="RTGS">RTGS</option>
                  <option value="upi">UPI</option>
                </select>
                <svg class="select-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
            </div>
            <div class="field">
              <label>From</label>
              <input type="date" v-model.trim="startDate" class="date-input" />
            </div>
            <div class="field">
              <label>To</label>
              <input type="date" v-model.trim="endDate" class="date-input" />
            </div>
          </div>
          <div class="advanced-foot">
            <button type="button" class="btn btn-ghost btn-danger" @click.stop="resetFilters">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="btn-icon">
                <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              Reset filters
            </button>
          </div>
        </div>
      </transition>
    </section>

    <!-- ==================== Body ==================== -->
    <div v-if="isLoading || loadingAvailable" class="po-loading">
      <div class="loading-spinner"></div>
      <div class="loading-label">Loading orders…</div>
    </div>

    <!-- Available payouts (bank/branch pickup view) -->
    <template v-else-if="viewMode === 'available' && (isUser || isSubAdmin)">
      <div v-if="availablePayouts.length === 0" class="empty-hero">
        <div class="empty-hero-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
            <circle cx="12" cy="12" r="10" />
            <path d="M8 12h8M12 8v8" stroke-linecap="round" />
          </svg>
        </div>
        <div class="empty-hero-title">No available payout orders</div>
        <div class="empty-hero-sub">Nothing to pick up right now. New orders appear here live.</div>
      </div>
      <div v-else class="po-body">
        <div class="po-grid">
          <pay-out-item
            v-for="(item, index) in availablePayouts"
            :key="index"
            :item="item"
            :sl-no="index + 1"
            :select-all="false"
            :clear-selected="false"
            @select-item="() => {}"
            @switch-view-mode="switchViewModeAndBack"
            :show-pickup-button="true"
            @pickup-order="assignPayoutOrder"
          ></pay-out-item>
        </div>
      </div>
    </template>

    <!-- Regular payout list -->
    <template v-else-if="hasPayOutList && !isLoading">
      <div class="po-body">
        <div class="po-grid">
          <pay-out-item
            v-for="(item, index) in filteredPayOutList"
            :key="index"
            :item="item"
            :sl-no="paginatorInfo.firstItem + index"
            :select-all="selectAll"
            :clear-selected="clearSelected"
            @select-item="selectItem"
            @switch-view-mode="switchViewModeAndBack"
            @refresh="getPayOutList"
          ></pay-out-item>
        </div>
        <div class="pagination-wrap">
          <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
        </div>
      </div>
    </template>

    <div v-else class="empty-hero">
      <div class="empty-hero-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
          <path d="M12 21V7M6 13l6 6 6-6M4 3h16" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </div>
      <div class="empty-hero-title">No pay-out transactions found</div>
      <div class="empty-hero-sub">
        {{ hasAnyFilter ? 'Try adjusting your filters or clearing the date range.' : 'New orders will appear here live.' }}
      </div>
    </div>

    <!-- ==================== Extract modal (teleported) ==================== -->
    <teleport to="body">
      <transition name="modal-fade">
        <div v-if="showPopUp" class="modal-backdrop" @click.self="closePopUp">
          <div class="modal-card">
            <div class="modal-head">
              <div>
                <div class="modal-eyebrow">Bulk export</div>
                <div class="modal-title">Extract pay-out orders</div>
                <div class="modal-sub">Generate a bank-specific extract for the selected orders.</div>
              </div>
              <button type="button" class="modal-close" @click="closePopUp">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                  <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
                </svg>
              </button>
            </div>
            <div class="modal-body">
              <div class="field">
                <label>Select the bank</label>
                <div class="select-wrap">
                  <select v-model.trim="extractBank">
                    <option value="">Select an option</option>
                    <option value="kotak">Kotak</option>
                    <option value="idfc">IDFC</option>
                  </select>
                  <svg class="select-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
              </div>
            </div>
            <div class="modal-foot">
              <button type="button" class="btn btn-secondary" @click.stop="closePopUp">Cancel</button>
              <button type="button" class="btn btn-primary" @click.stop="generateExtract">Generate</button>
            </div>
          </div>
        </div>
      </transition>
    </teleport>

    <!-- ==================== Create pay-out order modal (teleported) ==================== -->
    <teleport to="body">
      <transition name="modal-fade">
        <div v-if="createPayOutOrderModal" class="modal-backdrop" @click.self="closeCreatePayOutOrderModal">
          <div class="modal-card modal-card-wide">
            <div class="modal-head">
              <div>
                <div class="modal-eyebrow">Manual entry</div>
                <div class="modal-title">Create pay-out order</div>
                <div class="modal-sub">Fill in beneficiary details to create a new payout order.</div>
              </div>
              <button type="button" class="modal-close" @click="closeCreatePayOutOrderModal">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                  <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
                </svg>
              </button>
            </div>
            <div class="modal-body">
              <div v-if="isLoading" class="loading-block">
                <div class="loading-spinner"></div>
                <div class="loading-label">Creating…</div>
              </div>
              <div v-else ref="tab1" class="cpo-grid">
                <div class="field" :class="{ 'field-invalid': !accountNumber.isValid }">
                  <label>Account number</label>
                  <input
                    type="text"
                    v-model.trim="accountNumber.val"
                    class="input"
                    placeholder="Beneficiary account number"
                    @focus="clearValidity('accountNumber')"
                  />
                  <p v-if="!accountNumber.isValid" class="field-error">{{ accountNumber.msg }}</p>
                </div>
                <div class="field" :class="{ 'field-invalid': !customerName.isValid }">
                  <label>Customer name (account holder)</label>
                  <input
                    type="text"
                    maxlength="255"
                    v-model.trim="customerName.val"
                    class="input"
                    placeholder="Full name on the bank account"
                    @focus="clearValidity('customerName')"
                  />
                  <p v-if="!customerName.isValid" class="field-error">{{ customerName.msg }}</p>
                </div>
                <div class="field" :class="{ 'field-invalid': !ifsc.isValid }">
                  <label>IFSC</label>
                  <input
                    type="text"
                    v-model.trim="ifsc.val"
                    class="input mono"
                    placeholder="ABCD0123456"
                    @focus="clearValidity('ifsc')"
                  />
                  <p v-if="!ifsc.isValid" class="field-error">{{ ifsc.msg }}</p>
                </div>
                <div class="field" :class="{ 'field-invalid': !bankName.isValid }">
                  <label>Bank name</label>
                  <input
                    type="text"
                    v-model.trim="bankName.val"
                    class="input"
                    placeholder="e.g. HDFC Bank"
                    @focus="clearValidity('bankName')"
                  />
                  <p v-if="!bankName.isValid" class="field-error">{{ bankName.msg }}</p>
                </div>
                <div class="field field-full" :class="{ 'field-invalid': !payoutAmount.isValid }">
                  <label>Amount</label>
                  <div class="input-wrap">
                    <span class="input-prefix">₹</span>
                    <input
                      type="text"
                      v-model.trim="payoutAmount.val"
                      class="input has-prefix"
                      placeholder="0.00"
                      @focus="clearValidity('payoutAmount')"
                    />
                  </div>
                  <p v-if="!payoutAmount.isValid" class="field-error">{{ payoutAmount.msg }}</p>
                </div>
              </div>
            </div>
            <div class="modal-foot">
              <button type="button" class="btn btn-secondary" @click.stop="closeCreatePayOutOrderModal" :disabled="isLoading">Cancel</button>
              <button type="button" class="btn btn-primary" @click.stop="submitCreatePayOutOrder" :disabled="isLoading">Create</button>
            </div>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script>
import PayOutItem from "../../components/payout/PayOutItem.vue";
import http from "../../http-common";
import { getScopedSocket } from "../../utils/socket";
import Pagination from "../../components/shared/pagination.vue";
// xlsx is lazy-loaded inside exportData() and generateExtract() via
// `await import('xlsx')` so the ~700KB library isn't pulled into the main
// payout chunk. Aligns with how pay-in does it.
import { playNotificationSound } from "../../utils/notificationSound";

export default {
    name: "pay-in",
    components: {
        PayOutItem,
        Pagination,
    },
    data() {
        return {
            isLoading: false,
                  period: "today",
                  searchField: "utr",
                        periodChips: [
                      { value: "today", label: "Today" },
                      { value: "yesterday", label: "Yesterday" },
        { value: "2d", label: "Last 2 Days" },
                      { value: "7d", label: "Last 7 Days" },
                      { value: "30d", label: "Last 30 Days" },
                      { value: "custom", label: "Custom" },
                  ],
            // Spinner/error tied to the LATEST request — stale completions
            // must not clear or flip them.
            loadSeq: 0,
            searchDebounceTimer: null,
            error: null,
            baseToast: {
                type: "",
                message: "",
            },
            channel: null,
            showSearch: false,
            searchKeyWord: "",
            // `amount` (single equality) stayed in code so existing exact-
            // amount lookups still work, but the UI now exposes a range pair
            // instead. amount stays nullable; amountMin/amountMax drive the
            // backend's BETWEEN-style filter.
            amount: null,
            amountMin: null,
            amountMax: null,
            paymentStatus: "",
            paymentMode: "",
            validatorUsername: "",
            utrFilter: "",
            startDate: "",
            endDate: "",
            usersWithRole: [],
            // Branches under the current vendor (or all branches for
            // superadmin). Populated by getBranches() on mount and used by
            // the Branch filter dropdown.
            branches: [],
            // Clients under the current vendor (populated for vendor /
            // superadmin only). Used by the Client filter dropdown.
            clients: [],
            // Selected client for the filter. Empty = no client filter.
            // Sent as `clientName` query param — already in getOrders
            // allowedFields, no backend change needed.
            clientName: "",
            socket: null,
            first: 10,
            page: 1,
            selectedOrders: [],
            // Disables the Export button + swaps its label while the export
            // fetch is in flight, so a slow round-trip doesn't tempt
            // double-clicks (each click hits the API again).
            isExporting: false,
            // Base set — the visible chips are filtered per role by the
            // `visibleQuickStatuses` computed. Branch (subadmin) and bank
            // (user) have a dedicated "Available orders" view-mode tab
            // that already covers the Unassigned slice, so we hide the
            // duplicate chip for those roles. Vendor / admin / superadmin /
            // client see the full set.
            quickStatuses: [
                { value: "",           label: "All",        tone: "neutral" },
                { value: "pending",    label: "Pending",    tone: "pending" },
                { value: "approved",   label: "Approved",   tone: "success" },
                { value: "unassigned", label: "Unassigned", tone: "info"    },
                { value: "failed",     label: "Failed",     tone: "danger"  },
            ],
            viewMode: "my-orders", // "available" or "my-orders"
            availablePayouts: [],
            loadingAvailable: false,
            selectAll: false,
            showPopUp: false,
            extractBank: "",
            clearSelected: new Date().toISOString(),
            createPayOutOrderModal: false,
            accountNumber: {
                val: "",
                isValid: true,
                msg: "",
            },
            // Customer Name doubles as the bank-transfer Account Holder
            // Name. Backend stores it on orders.customerName.
            customerName: {
                val: "",
                isValid: true,
                msg: "",
            },
            ifsc: {
                val: "",
                isValid: true,
                msg: "",
            },
            bankName: {
                val: "",
                isValid: true,
                msg: "",
            },
            payoutAmount: {
                val: "",
                isValid: true,
                msg: "",
            },
            formIsValid: true,
        };
    },
    watch: {
        first() {
            this.selectAll = false;
            this.getPayOutList();
        },
        page() {
            this.selectAll = false;
            this.getPayOutList();
        },
        showSearch(newVal) {
            if (newVal) {
                // IST date — see istDay(); local/UTC here re-created the
                // "Today shows yesterday" bug through the custom pickers.
                this.startDate = this.endDate = this.istDay(0);
            }
            if (!newVal) {
                this.startDate = this.endDate = "";
                this.getPayOutList();
            }
        },
        // Typed inputs debounce: one request per pause, not one per
        // keystroke; the store action's request-seq guard drops any stale
        // response that resolves after a newer request's.
        searchKeyWord() {
            this.debouncedReload();
        },
        searchField() {
            if (String(this.searchKeyWord || "").trim()) this.debouncedReload();
        },
        amount() {
            this.debouncedReload();
        },
        amountMin() {
            // Refetch only when the range is consistent (or one side cleared).
            // Avoids a wasted round trip while the operator is mid-typing.
            if (this.amountRangeInvalid) return;
            this.debouncedReload();
        },
        amountMax() {
            if (this.amountRangeInvalid) return;
            this.debouncedReload();
        },
        paymentStatus() {
            this.selectAll = false;
            this.page = 1;
            this.getPayOutList();
        },
        validatorUsername() {
            this.selectAll = false;
            this.page = 1;
            this.getPayOutList();
        },
        clientName() {
            this.selectAll = false;
            this.page = 1;
            this.getPayOutList();
        },
        startDate(newVal) {
            if (this.endDate || newVal == "") {
                this.selectAll = false;
                this.page = 1;
                this.getPayOutList();
            }
        },
        endDate(newVal) {
            if (this.startDate || newVal == "") {
                this.selectAll = false;
                this.page = 1;
                this.getPayOutList();
            }
        },
        selectAll(newVal) {
            this.selectedOrders = [];
            if (newVal) {
                this.filteredPayOutList.forEach((row) => {
                    this.selectedOrders.push(row);
                });
            }
        },
        viewMode(newMode) {
            // Fetch data when switching views
            if (newMode === 'available') {
                this.fetchAvailablePayouts();
            } else {
                this.getPayOutList();
            }
        },
    },
    computed: {
        // True when both bounds are set and reversed. Drives the inline
        // validation message and gates the refetch watcher so we don't fire
        // an obviously-empty request.
        amountRangeInvalid() {
            return this.amountMin !== null
                && this.amountMin !== ''
                && this.amountMax !== null
                && this.amountMax !== ''
                && Number(this.amountMin) > Number(this.amountMax);
        },
        isUser() {
            return !!this.$store.getters.isUser;
        },
        isAdmin() {
            return !!this.$store.getters.isAdmin;
        },
        // Vendor-level operators see traffic across multiple branches, so a
        // Branch filter is meaningful for them. Banks / subadmins / clients
        // already see only their own scope — no value in showing the picker.
        canFilterByBranch() {
            const role = this.$store.getters.role;
            return role === 'admin' || role === 'superadmin';
        },
        isClient() {
            return !!this.$store.getters.isClient;
        },
        isSubAdmin() {
            return !!this.$store.getters.isSubAdmin;
        },
        paginatorInfo() {
            return this.$store.getters["payin/pagination"];
        },
        filteredPayOutList() {
            let list = this.$store.getters["payin/payInList"];

            // If in "available" view mode, show availablePayouts instead
            if (this.viewMode === 'available' && (this.isUser || this.isSubAdmin)) {
                return this.availablePayouts;
            }

            // If in "my-orders" view mode for banks, filter by validatorUsername
            if (this.viewMode === 'my-orders' && this.isUser) {
                list = list.filter(item => item.validatorUsername === this.username);
            }

            // Apply UTR filter
            if (this.utrFilter === 'with_utr') {
                list = list.filter(item => item.transactionID && item.transactionID.trim() !== '');
            } else if (this.utrFilter === 'without_utr') {
                list = list.filter(item => !item.transactionID || item.transactionID.trim() === '');
            }

            return list;
        },
        hasPayOutList() {
            return this.$store.getters["payin/hasPayInList"];
        },
        hasAnyFilter() {
            return !!(this.searchKeyWord || this.amount || this.amountMin || this.amountMax ||
                      this.paymentStatus || this.paymentMode || this.validatorUsername ||
                      this.clientName || this.utrFilter || this.startDate || this.endDate);
        },
        // Branch (subadmin) and bank (user) reach unassigned payouts via
        // the "Available orders" view-mode tab, so the Unassigned chip
        // would duplicate that same slice inside "My orders". Hide it for
        // those roles; vendor / admin / superadmin / client still see the
        // full chip set.
        visibleQuickStatuses() {
            if (this.isSubAdmin || this.isUser) {
                return this.quickStatuses.filter(s => s.value !== 'unassigned');
            }
            return this.quickStatuses;
        },
        role() {
            return this.$store.getters.role;
        },
        username() {
            return this.$store.getters.username;
        },
        agents() {
            return this.usersWithRole;
        },
        vendor() {
            return this.$store.getters.vendor;
        },
        userId() {
            return this.$store.getters.userId;
        },
        getStyle() {
            const divElement = this.$refs.tab1;
            if (divElement) {
                const divHeight = divElement.clientHeight;
                return `height: ${divHeight}px;`;
            } else {
                return "height: 272px;";
            }
        },
    },
    created() {
        this.socket = getScopedSocket();

        // Handle events
        this.socket.on("connect", () => {
            console.log("Connected to server");
        });

        this.socket.on("disconnect", () => {
            console.log("Disconnected from server");
        });

        this.getPayOutList();
        this.getUsersWithRole();
        if (this.canFilterByBranch) {
            this.getBranches();
            this.getClients();
        }

        // Scope subscriptions to the narrowest room the user owns, so a branch
        // doesn't receive sibling branches' payout traffic via the vendor room.
        // Defensive: subadmin on a legacy JWT without `id` falls back to
        // vendor-wide so they don't lose every event silently.
        let suffix;
        if (this.role == "admin" || this.role == "superadmin") {
            // Vendor-level operators (admin + superadmin) subscribe to the
            // vendor-wide room. Without the superadmin branch, they fell
            // through to `-${username}` — a room no backend emit targets —
            // and got zero realtime updates on the payout list.
            suffix = "";
        } else if (this.role == "subadmin") {
            if (!this.userId) {
                console.warn(
                    "[IndiaPayU] subadmin missing userId — likely a stale JWT. " +
                    "Falling back to vendor-wide socket room. Re-login to scope to your branch."
                );
                suffix = "";
            } else {
                suffix = `-branch-${this.userId}`;
            }
        } else if (this.role == "client") {
            // Clients listen on the dedicated client room (see PayIn for
            // the full explanation). clientName == JWT `name` == users.name.
            const clientName = this.$store.getters.name;
            if (!clientName) {
                console.warn("[IndiaPayU] client missing name — legacy JWT? Falling back to vendor-wide room.");
                suffix = "";
            } else {
                suffix = `-client-${clientName}`;
            }
        } else {
            suffix = `-${this.username}`;
        }

        this.socket.on(`${this.vendor}-payout-order-created${suffix}`, (data) => {
            this.addOrderToPayOutList(data);
            this.playSound('payout');
        });
        this.socket.on(`${this.vendor}-payout-order-amount-created${suffix}`, (data) => {
            this.updatePayOutAmountAndStatus(data);
        });
        this.socket.on(`${this.vendor}-payout-order-update-status-and-trnx${suffix}`, (data) => {
            this.updateStatusAndTransaction(data);
        });
        // Catch-all merge — see PayIn for context.
        this.socket.on(`${this.vendor}-order-updated${suffix}`, (data) => {
            this.handleOrderUpdated(data);
        });
        this.socket.on(`${this.vendor}-update-payout-link-order`, (data) => {
            this.updatePayoutLinkOrder(data);
        });
        this.socket.on("order-approved-auto", (data) => {
            this.autoOrderApproved(data);
        });

        // Listen for payout assignment events
        this.socket.on(`${this.vendor}-payout-order-assigned`, () => {
            // Refresh available payouts if in that view
            if (this.viewMode === 'available') {
                this.fetchAvailablePayouts();
            }
        });
    },
    methods: {
        playSound(type) {
            playNotificationSound(type);
        },
        toggleFilters() {
            this.showSearch = !this.showSearch;
        },
        resetFilters() {
            this.amount = null;
            this.amountMin = null;
            this.amountMax = null;
            this.paymentStatus = "";
            this.paymentMode = "";
            this.validatorUsername = "";
            this.clientName = "";
            this.utrFilter = "";
            this.startDate = "";
            this.endDate = "";
            this.selectAll = false;
            this.page = 1;
            this.getPayOutList();
        },
        setPage(page) {
            this.page = page;
        },
        sendMessage() {
            this.socket.emit("message", this.message);
            this.message = "";
        },
        async getUsersWithRole() {
            try {
                const response = await http.get("/users/user");

                if (response.status == 200) {
                    this.usersWithRole = response.data;
                }
            } catch (error) {
                console.log(error);
            }
        },
        // Populate the Branch filter dropdown. Backend `/users/:role` already
        // scopes to the requester's vendor (superadmin gets all). Branch =
        // role 'subadmin' (a subadmin user IS the branch per the data model).
        async getBranches() {
            try {
                const response = await http.get("/users/subadmin");
                if (response.status === 200) {
                    this.branches = response.data || [];
                }
            } catch (error) {
                console.log(error);
            }
        },
        // Populate the Client filter dropdown (vendor-only). /clients/
        // getClientName is vendor-scoped server-side. Tolerant of either
        // { clients: [...] } or { data: [...] } response shapes.
        async getClients() {
            try {
                const response = await http.get("/clients/getClientName?first=500&page=1");
                if (response.status === 200) {
                    const payload = response.data || {};
                    this.clients = payload.clients || payload.data || [];
                }
            } catch (error) {
                console.log(error);
            }
        },
        autoOrderApproved(data) {
            this.$store.commit("payin/setPaymentStatusAndUtr", data);
        },
        // Receives full-row updates from emitOrderUpdated. Scoped to payout
        // rows to avoid PayIn / PayOut store cross-contamination (the store
        // is shared so the list contains both types depending on which page
        // populated it last).
        handleOrderUpdated(data) {
            if (!data || data.type !== 'payout') return;
            this.$store.commit("payin/mergeOrderRow", data);
        },
        // Returns true if `order` would survive every active filter. When no
        // filter is set this is trivially true so unfiltered views keep
        // their realtime push behaviour. Used to gate new socket-pushed
        // payouts so a filtered list doesn't get polluted with non-matching
        // rows just because the order was created seconds ago.
        matchesActiveFilters(order) {
            if (!order) return false;
            // Client scope guard — see PayIn matchesActiveFilters for the
            // full rationale (fallback room can otherwise leak other
            // clients' new payouts into this client's list).
            if (this.role === 'client') {
                const myName = this.$store.getters.name;
                // Legacy JWT (no name claim) → drop every realtime push to
                // be safe; user must re-login to restore realtime.
                if (!myName) return false;
                if (order.clientName !== myName) return false;
            }
            if (this.paymentStatus && order.paymentStatus !== this.paymentStatus) return false;
            if (this.amount && Number(order.amount) !== Number(this.amount)) return false;
            // Range filter (independent bounds). Skip whichever side is unset.
            const orderAmt = Number(order.amount);
            if (this.amountMin !== null && this.amountMin !== '' && orderAmt < Number(this.amountMin)) return false;
            if (this.amountMax !== null && this.amountMax !== '' && orderAmt > Number(this.amountMax)) return false;
            if (this.validatorUsername && order.validatorUsername !== this.validatorUsername) return false;
            if (this.clientName && order.clientName !== this.clientName) return false;
            if (this.paymentMode && String(order.mode || '').toUpperCase() !== String(this.paymentMode).toUpperCase()) return false;
            if (this.utrFilter === 'with_utr' && !order.transactionID) return false;
            if (this.utrFilter === 'without_utr' && order.transactionID) return false;
            if (this.searchKeyWord) {
                const needle = String(this.searchKeyWord).toLowerCase();
                const haystack = [
                    order.customerName, order.merchantOrderId, order.receiptId,
                    order.transactionID, order.customerUPIID, order.customerUtr,
                    order.validatorUsername, order.refID, order.clientName,
                ].map((v) => String(v || '').toLowerCase()).join(' ');
                if (!haystack.includes(needle)) return false;
            }
            return true;
        },
        addOrderToPayOutList(data) {
            // Only push when we're on page 1 AND the incoming order matches
            // every active filter — otherwise a filtered view picks up rows
            // it shouldn't.
            if (this.page == 1 && data.type == "payout" && this.matchesActiveFilters(data)) {
                this.$store.commit("payin/addOrderToPayInList", data);
            }
        },
        updatePayOutAmountAndStatus(data) {
            if (!this.showSearch) {
                this.$store.commit("payin/updatePayOutAmountAndStatus", data);
            }
        },
        updateStatusAndTransaction(data) {
            if (!this.showSearch) {
                this.$store.commit("payin/updateStatusAndTransaction", data);
            }
        },
        updatePayoutLinkOrder(data) {
            if (!this.showSearch) {
                this.$store.commit("payin/updatePayoutLinkOrder", data);
            }
        },
        async getPayOutList() {
            // If in "available" view mode, fetch available payouts instead
            if (this.viewMode === 'available' && (this.isUser || this.isSubAdmin)) {
                await this.fetchAvailablePayouts();
                return;
            }
            const range = this.periodRange();
            if (!range) return; // custom period awaiting valid dates

            const params = {
                type: "payout",
                payoutType: 'standard',
                searchKeyWord: this.searchKeyWord,
                amount: this.amount,
                amountMin: this.amountMin,
                amountMax: this.amountMax,
                paymentStatus: this.paymentStatus,
                mode: this.paymentMode,
                validatorUsername: this.validatorUsername,
                // Vendor-only — clientName is already in getOrders
                // allowedFields, store action strips empties.
                clientName: this.clientName,
                startDate: range.startDate,
                endDate: range.endDate,
                searchField: this.searchField,
                first: this.first,
                page: this.page,
            };
            const seq = ++this.loadSeq;
            this.isLoading = true;
            try {
                await this.$store.dispatch("payin/getPayInList", params);
            } catch (error) {
                if (seq === this.loadSeq) this.error = error.message || "Something failed!";
            } finally {
                if (seq === this.loadSeq) this.isLoading = false;
            }
        },
        setPeriod(p) {
          this.period = p;
          if (p === "custom") {
            // Open the advanced panel so the date pickers are visible; fetch
            // happens when the operator picks valid dates (date watchers).
            this.showSearch = true;
            return;
          }
          if (this.page !== 1) this.page = 1;
          else this.getPayOutList();
        },
        // The server enforces the same rules — this is UX-side validation only.
        // IST calendar date for now+offset days. NEVER new Date().toISOString()
        // (browser clock → UTC): the backend interprets these strings in IST,
        // so any viewer west of IST — or any machine before 05:30 IST —
        // computed yesterday's date and saw yesterday's orders under "Today".
        // IST has no DST, so ms-offset day arithmetic is safe.
        istDay(offset = 0) {
          return new Intl.DateTimeFormat("en-CA", {
            timeZone: "Asia/Kolkata",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          }).format(new Date(Date.now() + offset * 86400000));
        },
        periodRange() {
          const day = (offset) => this.istDay(offset);
          if (this.period === "today") return { startDate: day(0), endDate: day(0) };
          if (this.period === "yesterday") return { startDate: day(-1), endDate: day(-1) };
      if (this.period === "2d") return { startDate: day(-1), endDate: day(0) };
          if (this.period === "7d") return { startDate: day(-6), endDate: day(0) };
          if (this.period === "30d") return { startDate: day(-29), endDate: day(0) };
          // custom
          if (!this.startDate || !this.endDate) return null; // wait for both
          const span = (new Date(this.endDate) - new Date(this.startDate)) / 86400000;
          if (span < 0) { this.error = "End date is before start date."; return null; }
          // Payout is exempt from the 31-day span cap (28102026) — same as
          // payin; archival holds live orders to ~60 days so wide ranges stay
          // index-bounded. Server matches (getOrders exempts type=payout).
          return { startDate: this.startDate, endDate: this.endDate };
        },
        debouncedReload() {
            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = setTimeout(() => {
                this.selectAll = false;
                // page watcher fetches on the page=1 assignment — avoid doubling.
                if (this.page !== 1) {
                    this.page = 1;
                } else {
                    this.getPayOutList();
                }
            }, 300);
        },
        async fetchAvailablePayouts() {
            // Same latest-wins rule for the direct available-payouts fetch —
            // it shares triggers (search/filter watchers) with the list.
            const seq = ++this.loadSeq;
            this.loadingAvailable = true;
            try {
                const params = {};
                if (this.amount) params.amount = this.amount;
                // Backend filters orders.mode by the `paymentMethod` query param.
                if (this.paymentMode) params.paymentMethod = this.paymentMode;

                const response = await http.get("/orders/payout/available", { params });

                if (seq !== this.loadSeq) return;
                if (response.data.success) {
                    this.availablePayouts = response.data.data || [];
                } else {
                    this.error = response.data.message || "Failed to fetch available payouts";
                }
            } catch (error) {
                if (seq === this.loadSeq) this.error = error.response?.data?.message || error.message || "Failed to fetch available payouts";
            } finally {
                if (seq === this.loadSeq) this.loadingAvailable = false;
            }
        },
        async assignPayoutOrder(refID, selectedBank = null) {
            try {
                const payload = {};
                if (selectedBank) {
                    payload.selectedBank = selectedBank;
                }

                const response = await http.post(`/orders/${refID}/assign-payout`, payload);

                if (response.data.success) {
                    this.baseToast = {
                        type: "success",
                        message: "Payout order assigned successfully"
                    };

                    // Refresh the list
                    if (this.viewMode === 'available') {
                        await this.fetchAvailablePayouts();
                    } else {
                        await this.getPayOutList();
                    }
                } else {
                    this.error = response.data.message || "Failed to assign payout order";
                }
            } catch (error) {
                this.error = error.response?.data?.message || error.message || "Failed to assign payout order";
            }
        },
        async exportData() {
            // Fetch every row that matches the current filters (capped at 5k)
            // and build the XLSX from THAT — not from the paginated store
            // slice. Previously "Export" only dumped the current page.
            if (this.isExporting) return;
            // Hard gate: both date bounds must be set. Without them an export
            // could sweep up the entire table (capped at 5k) and quietly
            // truncate. Forcing a range also keeps the file small.
            if (!this.startDate || !this.endDate) {
                this.$swal.fire({
                    icon: 'warning',
                    title: 'Date range required',
                    text: 'Please pick both "From" and "To" dates before exporting.',
                });
                return;
            }
            this.isExporting = true;
            try {
                const EXPORT_CAP = 5000;
                const qs = new URLSearchParams();
                qs.set('type', 'payout');
                qs.set('payout_type', 'standard');
                if (this.role === 'user') qs.set('validatorUsername', this.username);
                if (this.searchKeyWord) qs.set('searchKeyWord', this.searchKeyWord);
                if (this.amount) qs.set('amount', this.amount);
                // Range bounds — independent, either/both/none.
                if (this.amountMin !== null && this.amountMin !== '') qs.set('amountMin', String(this.amountMin));
                if (this.amountMax !== null && this.amountMax !== '') qs.set('amountMax', String(this.amountMax));
                if (this.paymentStatus) qs.set('paymentStatus', this.paymentStatus);
                if (this.validatorUsername) qs.set('validatorUsername', this.validatorUsername);
                if (this.clientName) qs.set('clientName', this.clientName);
                if (this.paymentMode) qs.set('mode', this.paymentMode);
                if (this.startDate && this.endDate) {
                    qs.set('startDate', this.startDate);
                    qs.set('endDate', this.endDate);
                }
                qs.set('first', String(EXPORT_CAP));
                qs.set('page', '1');

                const response = await http.get(`/orders?${qs.toString()}`);
                if (response.status !== 200) throw new Error('Server returned ' + response.status);
                let rows = (response.data && response.data.data) || [];

                // utrFilter is a client-side toggle (no backend equivalent),
                // so mirror filteredPayOutList's narrowing here. On payouts
                // the UTR lives in transactionID, not customerUtr.
                if (this.utrFilter === 'with_utr') rows = rows.filter(r => r.transactionID);
                else if (this.utrFilter === 'without_utr') rows = rows.filter(r => !r.transactionID);

                if (rows.length === 0) {
                    this.$swal.fire({
                        icon: 'info',
                        title: 'Nothing to export',
                        text: 'No Pay-Out orders match the current filters.',
                    });
                    return;
                }

                const num = (v) => {
                    const n = Number(v);
                    return Number.isFinite(n) ? n : '';
                };
                // createdAt / updatedAt are stored as raw IST strings (see
                // CLAUDE.md) but JSON-serialized with a misleading "Z" suffix.
                // Strip the T/Z and ms so Excel shows the IST clock time.
                const fmt = (v) => String(v || '').replace('T', ' ').replace(/\.\d+Z?$/, '').replace(/Z$/, '');
                const approvedAt = (item) => item.paymentStatus === 'approved' ? fmt(item.updatedAt) : '';

                // Column-level scoping per role. Row scoping is enforced by
                // the backend; this trims columns so we don't leak vendor /
                // branch / operator info to roles that shouldn't see it.
                //   - client: hide Branch + Approved By Username + Branches.
                //   - user (bank): hide Vendor + Client Name + Branches.
                //   - subadmin (branch): hide Client Name + Branches +
                //     Requested Amount. Requested Amount is the ORIGINAL
                //     order total — branches should only see what they
                //     paid / need to pay (the existing Amount + Remaining
                //     Amount columns already give them that), never the
                //     original on a partial order. Mirrors the UI rule
                //     (PayOutItem.vue → branchHidesOriginal).
                //   - admin / superadmin: see everything including Branches.
                const HIDDEN_COLS_BY_ROLE = {
                    client: new Set(['Branch', 'Approved By Username', 'Branches']),
                    user: new Set(['Vendor', 'Client Name', 'Branches']),
                    subadmin: new Set(['Client Name', 'Branches', 'Requested Amount']),
                };
                const hidden = HIDDEN_COLS_BY_ROLE[this.role];

                // Multi-branch attribution. Only fetch the settlement summary
                // when the viewer is vendor-level (role='admin' per CLAUDE.md)
                // or superadmin AND there's at least one partial-settled row
                // in the export — small set in practice. The batch endpoint
                // maps refID → branch list.
                const canSeeBranchesCol = ['admin', 'superadmin'].includes(this.role);
                let settlementSummary = {};
                if (canSeeBranchesCol) {
                    const refIDsWithSettlement = rows
                        .filter(r => Number(r.approved_amount) > 0)
                        .map(r => r.refID)
                        .filter(Boolean);
                    if (refIDsWithSettlement.length > 0) {
                        try {
                            const resp = await http.post(
                                '/orders/payout-settlement-summary',
                                { refIDs: refIDsWithSettlement }
                            );
                            if (resp.status === 200 && resp.data && resp.data.data) {
                                settlementSummary = resp.data.data;
                            }
                        } catch (e) {
                            // Non-fatal: export proceeds without the column.
                            // The frontend logs but doesn't block — operators
                            // can still get the other fields.
                            console.warn('settlement-summary fetch failed', e);
                        }
                    }
                }

                const data = rows.map(item => ({
                    // For payouts validatorUsername IS the branch (subadmin
                    // username) under the new branch-pickup flow. Drop the
                    // misleading "Branch User Name" wording and just label
                    // it "Branch". Customer UTR is omitted — it's a pay-in
                    // field and is always empty on payout rows.
                    'Branch': item.validatorUsername || '',
                    // Per-order branch attribution for multi-branch payouts.
                    // Joined string (eg "branchA, branchB"); single-settler
                    // orders show one name; non-settled rows are blank.
                    'Branches': (settlementSummary[item.refID] && settlementSummary[item.refID].branches.join(', ')) || '',
                    'Receipt ID': item.receiptId || '',
                    'Order ID': item.merchantOrderId || '',
                    'Vendor': item.vendor || '',
                    'Client Name': item.clientName || '',
                    'Type': item.type || '',
                    'Customer Name': item.customerName || '',
                    // For approved partial payouts the Amount column shows the
                    // paid portion (approved_amount), not the original
                    // requested amount — matches the dashboard fix. Mirrors
                    // the SQL pattern COALESCE(NULLIF(approved_amount, 0), amount):
                    // approved_amount > 0 wins; otherwise fall back to amount.
                    'Amount': num(
                        Number(item.approved_amount) > 0
                            ? item.approved_amount
                            : item.amount
                    ),
                    'Requested Amount': num(item.amount),
                    'Remaining Amount': num(item.remaining_amount || 0),
                    'Payment Status': item.paymentStatus || '',
                    // "Mode" is the payout rail (IMPS/NEFT/RTGS/UPI). The old
                    // "Payment Method" column (orders.paymentMethod) is dropped
                    // here because it's the validator's bank-automation profile
                    // and is empty for branch-driven payouts.
                    'Mode': item.mode || '',
                    'Transaction ID': item.transactionID || '',
                    'Approved By Username': item.approvedByUsername || '',
                    'Created At': fmt(item.createdAt),
                    'Approved At': approvedAt(item),
                }));

                // Import the self-contained UMD bundle, not the package's main
                // entry. Under Vue CLI 4 / webpack 4 the `await import('xlsx')`
                // path can resolve to a module wrapper that throws "Cannot
                // read properties of undefined (reading 'call')" at runtime;
                // the UMD bundle loads cleanly. The `.default || mod` unwrap
                // handles both the `__esModule`-flagged and bare UMD cases.
                // Apply role-based column hiding right before serializing.
                const scoped = hidden
                    ? data.map(row => {
                        const out = {};
                        for (const k of Object.keys(row)) {
                            if (!hidden.has(k)) out[k] = row[k];
                        }
                        return out;
                    })
                    : data;

                const xlsxMod = await import('xlsx/dist/xlsx.full.min.js');
                const XLSX = xlsxMod.default || xlsxMod;
                const ws = XLSX.utils.json_to_sheet(scoped);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, 'PayOut');
                // Filename uses IST date so it matches what the operator sees
                // in-app. UTC was off by up to a day near IST midnight.
                const istDate = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
                XLSX.writeFile(wb, `payout-export-${istDate}.xlsx`);

                if (rows.length >= EXPORT_CAP) {
                    this.$swal.fire({
                        icon: 'warning',
                        title: 'Export truncated',
                        text: `Only the first ${EXPORT_CAP} rows were exported. Narrow your filters to capture the rest.`,
                    });
                }
            } catch (error) {
                // Server (axios) errors carry the API message under
                // .response.data.message; generic Errors carry .message;
                // everything else stringifies. Cover all so the dialog never
                // says "Export failed: undefined".
                const msg =
                    (error && error.response && error.response.data && error.response.data.message) ||
                    (error && error.message) ||
                    (error ? String(error) : 'unknown error');
                this.error = 'Export failed: ' + msg;
            } finally {
                this.isExporting = false;
            }
        },
        handleError() {
            this.error = null;
        },
        searchBtnClicked() {
            this.showSearch = true;
        },
        cancelBtnClicked() {
            this.showSearch = false;
            this.searchKeyWord = "";
            this.amount = null;
            this.amountMin = null;
            this.amountMax = null;
            this.paymentStatus = "";
            this.validatorUsername = "";
            this.startDate = "";
            this.endDate = "";
        },
        extractBtnClicked() {
            if (this.selectedOrders.length > 0) {
                this.showPopUp = true;
            } else {
                let error_msg = "You must select at least 1 order!"
                this.$swal.fire({
                    icon: 'error',
                    title: 'Oops...',
                    text: error_msg,
                }).then((result) => {
                    if (result.value || result.dismiss) {
                        error_msg = null
                    }
                })
            }
        },
        closePopUp() {
            this.showPopUp = false;
        },
        async generateExtract() {
            // Lazy-load xlsx so the static top-of-file import (which bundled
            // the heavy ~700KB library into the main payout chunk) can be
            // removed. Use the UMD dist bundle (`.default || mod` unwrap)
            // because the package's main entry doesn't load cleanly under
            // Vue CLI 4 / webpack 4 — see exportData() above for context.
            const xlsxMod = await import('xlsx/dist/xlsx.full.min.js');
            const xlsx = xlsxMod.default || xlsxMod;
            if (this.extractBank == "") {
                let error_msg = "You must select a bank!"
                this.$swal.fire({
                    icon: 'error',
                    title: 'Oops...',
                    text: error_msg,
                }).then((result) => {
                    if (result.value || result.dismiss) {
                        error_msg = null
                    }
                })
            } else {

                if (this.extractBank == "kotak") {

                    let extractData = [];

                    this.selectedOrders.forEach((order) => {

                        const updatedAt = "2023-12-03T18:39:18.000Z";
                        const date = new Date(updatedAt);
                        const Payment_Date = `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;

                        const obj = {
                            Client_Code: "",
                            Product_Code: "",
                            Payment_Type: "",
                            Payment_Ref_No: order.refID,
                            Payment_Date: Payment_Date,
                            Instrument_Date: "",
                            Dr_Ac_No: "",
                            Amount: order.amount,
                            Bank_Code_Indicator: "",
                            Beneficiary_Code: "",
                            Beneficiary_Name: order.customerName,
                            Beneficiary_Bank: order.bankName,
                            Beneficiary_Branch_OR_IFSC_Code: order.ifsc,
                            Beneficiary_Acc_No: order.accountNumber,
                            Location: "",
                            Print_Location: "",
                            Instrument_Number: "",
                            Ben_Add1: "",
                            Ben_Add2: "",
                            Ben_Add3: "",
                            Ben_Add4: "",
                            Beneficiary_Email: "",
                            Beneficiary_Mobile: order.customerMobile,
                            Debit_Narration: "",
                            Credit_Narration: "",
                            Payment_Details_1: "",
                            Payment_Details_2: "",
                            Payment_Details_3: "",
                            Payment_Details_4: "",
                            Enrichment_1: "",
                            Enrichment_2: "",
                            Enrichment_3: "",
                            Enrichment_4: "",
                            Enrichment_5: "",
                            Enrichment_6: "",
                            Enrichment_7: "",
                            Enrichment_8: "",
                            Enrichment_9: "",
                            Enrichment_10: "",
                            Enrichment_11: "",
                            Enrichment_12: "",
                            Enrichment_13: "",
                            Enrichment_14: "",
                            Enrichment_15: "",
                            Enrichment_16: "",
                            Enrichment_17: "",
                            Enrichment_18: "",
                            Enrichment_19: "",
                            Enrichment_20: "",
                        }

                        extractData.push(obj);
                    });

                    const XLSX = xlsx;

                    const header = [
                        "Client_Code",
                        "Product_Code",
                        "Payment_Type",
                        "Payment_Ref_No",
                        "Payment_Date",
                        "Instrument Date",
                        "Dr_Ac_No",
                        "Amount",
                        "Bank_Code_Indicator",
                        "Beneficiary_Code",
                        "Beneficiary_Name",
                        "Beneficiary_Bank",
                        "Beneficiary_Branch / IFSC Code",
                        "Beneficiary_Acc_No",
                        "Location",
                        "Print_Location",
                        "Instrument_Number",
                        "Ben_Add1",
                        "Ben_Add2",
                        "Ben_Add3",
                        "Ben_Add4",
                        "Beneficiary_Email",
                        "Beneficiary_Mobile",
                        "Debit_Narration",
                        "Credit_Narration",
                        "Payment Details 1",
                        "Payment Details 2",
                        "Payment Details 3",
                        "Payment Details 4",
                        "Enrichment_1",
                        "Enrichment_2",
                        "Enrichment_3",
                        "Enrichment_4",
                        "Enrichment_5",
                        "Enrichment_6",
                        "Enrichment_7",
                        "Enrichment_8",
                        "Enrichment_9",
                        "Enrichment_10",
                        "Enrichment_11",
                        "Enrichment_12",
                        "Enrichment_13",
                        "Enrichment_14",
                        "Enrichment_15",
                        "Enrichment_16",
                        "Enrichment_17",
                        "Enrichment_18",
                        "Enrichment_19",
                        "Enrichment_20",
                    ]

                    const worksheet = XLSX.utils.json_to_sheet(extractData);

                    worksheet["A1"] = { v: header[0], t: "s" }
                    worksheet["B1"] = { v: header[1], t: "s" }
                    worksheet["C1"] = { v: header[2], t: "s" }
                    worksheet["D1"] = { v: header[3], t: "s" }
                    worksheet["E1"] = { v: header[4], t: "s" }
                    worksheet["F1"] = { v: header[5], t: "s" }
                    worksheet["G1"] = { v: header[6], t: "s" }
                    worksheet["H1"] = { v: header[7], t: "s" }
                    worksheet["I1"] = { v: header[8], t: "s" }
                    worksheet["J1"] = { v: header[9], t: "s" }
                    worksheet["K1"] = { v: header[10], t: "s" }
                    worksheet["L1"] = { v: header[11], t: "s" }
                    worksheet["M1"] = { v: header[12], t: "s" }
                    worksheet["N1"] = { v: header[13], t: "s" }
                    worksheet["O1"] = { v: header[14], t: "s" }
                    worksheet["P1"] = { v: header[15], t: "s" }
                    worksheet["Q1"] = { v: header[16], t: "s" }
                    worksheet["R1"] = { v: header[17], t: "s" }
                    worksheet["S1"] = { v: header[18], t: "s" }
                    worksheet["T1"] = { v: header[19], t: "s" }
                    worksheet["U1"] = { v: header[20], t: "s" }
                    worksheet["V1"] = { v: header[21], t: "s" }
                    worksheet["W1"] = { v: header[22], t: "s" }
                    worksheet["X1"] = { v: header[23], t: "s" }
                    worksheet["Y1"] = { v: header[24], t: "s" }
                    worksheet["Z1"] = { v: header[25], t: "s" }
                    worksheet["AA1"] = { v: header[26], t: "s" }
                    worksheet["AB1"] = { v: header[27], t: "s" }
                    worksheet["AC1"] = { v: header[28], t: "s" }
                    worksheet["AD1"] = { v: header[29], t: "s" }
                    worksheet["AE1"] = { v: header[30], t: "s" }
                    worksheet["AF1"] = { v: header[31], t: "s" }
                    worksheet["AG1"] = { v: header[32], t: "s" }
                    worksheet["AH1"] = { v: header[33], t: "s" }
                    worksheet["AI1"] = { v: header[34], t: "s" }
                    worksheet["AJ1"] = { v: header[35], t: "s" }
                    worksheet["AK1"] = { v: header[36], t: "s" }
                    worksheet["AL1"] = { v: header[37], t: "s" }
                    worksheet["AM1"] = { v: header[38], t: "s" }
                    worksheet["AN1"] = { v: header[39], t: "s" }
                    worksheet["AO1"] = { v: header[40], t: "s" }
                    worksheet["AP1"] = { v: header[41], t: "s" }
                    worksheet["AQ1"] = { v: header[42], t: "s" }
                    worksheet["AR1"] = { v: header[43], t: "s" }
                    worksheet["AS1"] = { v: header[44], t: "s" }
                    worksheet["AT1"] = { v: header[45], t: "s" }
                    worksheet["AU1"] = { v: header[46], t: "s" }
                    worksheet["AV1"] = { v: header[47], t: "s" }
                    worksheet["AW1"] = { v: header[48], t: "s" }

                    worksheet['!cols'] = [
                        { width: 20 }, { width: 20 }, { width: 20 }, { width: 40 }, { width: 20 },
                        { width: 20 }, { width: 20 }, { width: 20 }, { width: 20 }, { width: 20 },
                        { width: 30 }, { width: 30 }, { width: 40 }, { width: 20 }, { width: 20 },
                        { width: 20 }, { width: 20 }, { width: 20 }, { width: 20 }, { width: 20 },
                        { width: 20 }, { width: 20 }, { width: 20 }, { width: 20 }, { width: 20 },
                        { width: 20 }, { width: 20 }, { width: 20 }, { width: 20 }, { width: 20 },
                        { width: 20 }, { width: 20 }, { width: 20 }, { width: 20 }, { width: 20 },
                        { width: 20 }, { width: 20 }, { width: 20 }, { width: 20 }, { width: 20 },
                        { width: 20 }, { width: 20 }, { width: 20 }, { width: 20 }, { width: 20 },
                        { width: 20 }, { width: 20 }, { width: 20 }, { width: 20 },
                    ]

                    const workbook = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
                    XLSX.writeFile(workbook, `${this.extractBank}.xlsx`);
                } else if (this.extractBank == "idfc") {
                    let extractData = [];

                    this.selectedOrders.forEach((order) => {

                        const updatedAt = "2023-12-03T18:39:18.000Z";
                        const date = new Date(updatedAt);
                        const Transaction_Date = `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;

                        const obj = {
                            Beneficiary_Name: order.customerName,
                            Beneficiary_Acc_No: order.accountNumber,
                            IFSC: order.ifsc,
                            Transaction_Type: "",
                            Debit_Account_Number: "",
                            Transaction_Date: Transaction_Date,
                            Amount: order.amount,
                            Currency: "INR",
                            Beneficiary_Email_ID: "",
                            Remarks: "",
                            Custom_Header_1: "",
                            Custom_Header_2: "",
                            Custom_Header_3: "",
                            Custom_Header_4: "",
                            Custom_Header_5: "",
                        }

                        extractData.push(obj);
                    });

                    const XLSX = xlsx;

                    const header = [
                        "Beneficiary Name",
                        "Beneficiary Account Number",
                        "IFSC",
                        "Transaction Type",
                        "Debit Account Number",
                        "Transaction Date",
                        "Amount",
                        "Currency",
                        "Beneficiary Email ID",
                        "Remarks",
                        "Custom Header-1",
                        "Custom Header-2",
                        "Custom Header-3",
                        "Custom Header-4",
                        "Custom Header-5",
                    ]

                    const worksheet = XLSX.utils.json_to_sheet(extractData);

                    worksheet["A1"] = { v: header[0], t: "s" }
                    worksheet["B1"] = { v: header[1], t: "s" }
                    worksheet["C1"] = { v: header[2], t: "s" }
                    worksheet["D1"] = { v: header[3], t: "s" }
                    worksheet["E1"] = { v: header[4], t: "s" }
                    worksheet["F1"] = { v: header[5], t: "s" }
                    worksheet["G1"] = { v: header[6], t: "s" }
                    worksheet["H1"] = { v: header[7], t: "s" }
                    worksheet["I1"] = { v: header[8], t: "s" }
                    worksheet["J1"] = { v: header[9], t: "s" }
                    worksheet["K1"] = { v: header[10], t: "s" }
                    worksheet["L1"] = { v: header[11], t: "s" }
                    worksheet["M1"] = { v: header[12], t: "s" }
                    worksheet["N1"] = { v: header[13], t: "s" }
                    worksheet["O1"] = { v: header[14], t: "s" }

                    worksheet['!cols'] = [
                        { width: 30 }, { width: 30 }, { width: 20 }, { width: 20 }, { width: 20 },
                        { width: 20 }, { width: 20 }, { width: 20 }, { width: 20 }, { width: 20 },
                        { width: 20 }, { width: 20 }, { width: 20 }, { width: 20 }, { width: 20 },
                    ]

                    const workbook = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
                    XLSX.writeFile(workbook, `${this.extractBank}.xlsx`);
                }

                this.showPopUp = false;
                this.extractBank = "";
                this.selectAll = false;
                this.clearSelected = new Date().toISOString();
            }
        },
        selectItem(refID, isSelected) {
            if (isSelected) {
                const selectedOrder = this.filteredPayOutList.find(order => order.refID === refID);
                this.selectedOrders.push(selectedOrder);
            } else {
                const index = this.selectedOrders.findIndex(order => order.refID === refID);
                if (index !== -1) {
                    this.selectedOrders.splice(index, 1);
                }
            }
        },
        openCreatePayOutOrderModal() {
            this.createPayOutOrderModal = true;
        },
        closeCreatePayOutOrderModal() {
            this.createPayOutOrderModal = false;
            this.accountNumber.val = "";
            this.accountNumber.isValid = true;
            this.accountNumber.msg = "";

            this.customerName.val = "";
            this.customerName.isValid = true;
            this.customerName.msg = "";

            this.ifsc.val = "";
            this.ifsc.isValid = true;
            this.ifsc.msg = "";

            this.bankName.val = "";
            this.bankName.isValid = true;
            this.bankName.msg = "";

            this.payoutAmount.val = "";
            this.payoutAmount.isValid = true;
            this.payoutAmount.msg = "";
        },
        clearValidity(input) {
            this[input].isValid = true;
            this[input].msg = "";
        },
        validateForm() {
            this.formIsValid = true;

            if (this.accountNumber.val === "") {
                this.accountNumber.isValid = false;
                this.accountNumber.msg = "Account Number must not be empty.";
                this.formIsValid = false;
            }
            if (this.customerName.val === "") {
                this.customerName.isValid = false;
                this.customerName.msg = "Customer Name (Account Holder) must not be empty.";
                this.formIsValid = false;
            }
            if (this.ifsc.val === "") {
                this.ifsc.isValid = false;
                this.ifsc.msg = "IFSC must not be empty.";
                this.formIsValid = false;
            }
            if (this.bankName.val === "") {
                this.bankName.isValid = false;
                this.bankName.msg = "Bank Name must not be empty.";
                this.formIsValid = false;
            }
            if (this.payoutAmount.val === "") {
                this.payoutAmount.isValid = false;
                this.payoutAmount.msg = "Amount must not be empty.";
                this.formIsValid = false;
            } else {
                const numberRegex = /^-?\d+(\.\d+)?$/;
                const isInteger = numberRegex.test(this.payoutAmount.val);
                if (!isInteger) {
                    this.payoutAmount.isValid = false;
                    this.payoutAmount.msg = "Please enter a valid amount.";
                    this.formIsValid = false;
                } else {
                    if (this.payoutAmount.val <= 0) {
                        this.payoutAmount.isValid = false;
                        this.payoutAmount.msg = "The amount should be greater than zero.";
                        this.formIsValid = false;
                    }
                }
            }

        },
        async submitCreatePayOutOrder() {
            this.validateForm();

            if (!this.formIsValid) {
                return;
            }

            this.isLoading = true;

            const formData = {
                accountNumber: this.accountNumber.val,
                // Beneficiary name — stored on orders.customerName; doubles
                // as the Account Holder Name on the bank-transfer ticket.
                customerName: this.customerName.val,
                ifsc: this.ifsc.val,
                bankName: this.bankName.val,
                amount: parseFloat(this.payoutAmount.val),
            }

            let url = `/orders/${this.vendor}/createPayOutOrder`;
            try {
                const response = await http.post(url, formData);
                if (response.status == 201) {
                    this.closeCreatePayOutOrderModal();
                    this.baseToast.type = "success";
                    this.baseToast.message = response.data.message;
                    setTimeout(() => {
                        this.handleBaseToast();
                        // this.getPayOutList();
                    }, 1000);
                }
            } catch (error) {
                console.log(error);
            }

            this.isLoading = false;

        },
        handleBaseToast() {
            this.baseToast = {
                type: "",
                message: "",
            };
        },
        switchViewModeAndBack() {
            // Switch to available view then back to my-orders to trigger refresh
            const originalMode = this.viewMode;
            this.viewMode = 'available';
            setTimeout(() => {
                this.viewMode = originalMode;
            }, 100);
        },
    },
    beforeUnmount() {
        clearTimeout(this.searchDebounceTimer);
        if (this.socket) {
            this.socket.disconnect();
        }
    },
};
</script>

<style scoped>
/* ==================== Shell ==================== */
.po-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 20px;
  background: linear-gradient(180deg, #fbfaf7 0%, #f4f0e5 100%);
  overflow-y: auto;
}

/* ==================== Header ==================== */
.po-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8a8371;
  font-weight: 600;
  margin-bottom: 4px;
}
.po-header h1 {
  font-size: 28px;
  font-weight: 700;
  color: #1b2a47;
  line-height: 1.1;
  margin: 0;
  letter-spacing: -0.01em;
}
.subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0;
  max-width: 640px;
  line-height: 1.5;
}
.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

/* ==================== View mode tabs ==================== */
.po-viewmode {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  background: #fff;
  border: 1px solid #ede9df;
  border-radius: 12px;
  align-self: flex-start;
}
.vm-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 600;
  color: #64748b;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;
}
.vm-tab svg { width: 14px; height: 14px; opacity: 0.7; }
.vm-tab:hover { background: #fbfaf7; color: #1b2a47; }
.vm-tab.is-active {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
}
.vm-tab.is-active svg { opacity: 1; }

/* ==================== Buttons ==================== */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
  outline: none;
  font-family: inherit;
  text-decoration: none;
}
.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn-primary {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 6px 16px -6px rgba(27, 42, 71, 0.45);
}
.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px -8px rgba(27, 42, 71, 0.55);
}
.btn-secondary {
  background: #fff;
  color: #475569;
  border-color: #ede9df;
}
.btn-secondary:hover:not(:disabled) { background: #fbfaf7; border-color: #cbd5e1; color: #1b2a47; }
.btn-ghost {
  background: transparent;
  color: #64748b;
  border-color: #ede9df;
}
.btn-ghost:hover:not(:disabled) { background: #f4f0e5; color: #1b2a47; }
.btn-ghost.is-active { background: #eef2f7; color: #1B2A47; border-color: #cbd5e1; }
.btn-ghost.btn-danger { color: #b91c1c; border-color: #fecaca; }
.btn-ghost.btn-danger:hover { background: #fef2f2; color: #991b1b; }
.btn-icon-only {
  padding: 0;
  width: 40px; height: 40px;
  background: #fff;
  color: #475569;
  border-color: #ede9df;
  justify-content: center;
}
.btn-icon-only:hover:not(:disabled) { background: #fbfaf7; color: #1b2a47; border-color: #cbd5e1; }
.btn-icon-only svg { width: 16px; height: 16px; }
.btn-icon { width: 15px; height: 15px; }

.spinner-mini {
  width: 14px; height: 14px;
  border-radius: 999px;
  border: 2px solid #cbd5e1;
  border-top-color: #1B2A47;
  animation: spin 0.7s linear infinite;
  display: inline-block;
}
.btn-primary .spinner-mini { border-color: rgba(255,255,255,0.35); border-top-color: #fff; }

/* ==================== Filters ==================== */
.po-filters {
  background: #fff;
  border: 1px solid #ede9df;
  border-radius: 14px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}
/* Joined scoped-search control (10082026): the WRAPPER owns the chrome —
   one border, one background, one radius, one hover/focus ring — so the
   scope segment and the input can never look like two controls. The
   input's own border/background/shadow are stripped when joined. */
.search-wrap {
  position: relative;
  flex: 1;
  min-width: 260px;
  display: flex;
  align-items: stretch;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fbfaf7;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.search-wrap:hover { border-color: #cbd5e1; }
.search-wrap:focus-within {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(27, 42, 71, 0.08);
}
.search-scope {
  display: flex;
  align-items: center;
  position: relative;
  border-right: 1px solid #e2e8f0;
  border-radius: 10px 0 0 10px;
  color: #475569;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
}
.search-scope:hover { color: #1B2A47; }
.search-scope select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border: 0;
  background: transparent;
  font: inherit;
  color: inherit;
  padding: 9px 24px 9px 12px;
  cursor: pointer;
  outline: none;
  border-radius: 10px 0 0 10px;
}
/* Browsers paint their own focus ring/background on <select> even with
   outline:none on the base rule — that UA artifact is what made the seam
   look broken when focusing the scope instead of the input. Kill every
   variant; the wrapper's :focus-within ring is the only focus visual. */
.search-scope select:focus,
.search-scope select:focus-visible,
.search-scope select:active {
  outline: none;
  box-shadow: none;
  background: transparent;
}
.search-scope select::-moz-focus-inner { border: 0; }
/* While the control is focused (either half), tint the divider to match
   the ring instead of leaving a stray light-grey line mid-pill. */
.search-wrap:focus-within .search-scope { border-right-color: rgba(27, 42, 71, 0.25); }
.scope-chevron {
  width: 12px;
  height: 12px;
  position: absolute;
  right: 8px;
  pointer-events: none;
  color: #64748b;
}
.search-box {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
}
.search-input.search-input-joined,
.search-input.search-input-joined:focus {
  border: 0;
  background: transparent;
  box-shadow: none;
  border-radius: 0 10px 10px 0;
}
.period-select { min-width: 118px; }
.search-icon {
  position: absolute;
  left: 12px;
  width: 16px; height: 16px;
  color: #94a3b8;
  pointer-events: none;
}
.search-input {
  width: 100%;
  padding: 9px 34px 9px 36px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #fbfaf7;
  outline: none;
  color: #0f172a;
  font-family: inherit;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.search-input:focus {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(27, 42, 71, 0.08);
}
.search-clear {
  position: absolute;
  right: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #94a3b8;
  width: 24px; height: 24px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.12s;
}
.search-clear:hover { background: #f1f5f9; color: #475569; }
.search-clear svg { width: 14px; height: 14px; }

.chip-group { display: flex; gap: 4px; flex-wrap: wrap; }
.chip {
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 12px;
  color: #475569;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.12s;
  font-family: inherit;
}
.chip:not(.chip-active):hover { border-color: #cbd5e1; background: #fbfaf7; }
.chip-active {
  background: #eef2f7;
  border-color: #cbd5e1;
  color: #1B2A47;
  font-weight: 600;
}
.chip-active-pending { background: #fef3c7; border-color: #fde68a; color: #b45309; }
.chip-active-success { background: #d1fae5; border-color: #a7f3d0; color: #047857; }
.chip-active-danger  { background: #fee2e2; border-color: #fecaca; color: #991b1b; }
.chip-active-info    { background: #dbeafe; border-color: #bfdbfe; color: #1e40af; }
.chip-active-neutral { background: #eef2f7; border-color: #cbd5e1; color: #1B2A47; }

/* ---------- Advanced filters ---------- */
.filters-advanced {
  padding: 14px;
  background: #fbfaf7;
  border: 1px solid #f1ede2;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.advanced-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
}
.field { display: flex; flex-direction: column; gap: 4px; }
.field-range { grid-column: span 2; }
.field label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}
.range-inputs {
  display: flex;
  align-items: center;
  gap: 8px;
}
.range-inputs .input-wrap { flex: 1; }
.range-sep {
  font-size: 11px;
  color: #94a3b8;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.input-prefix {
  position: absolute;
  left: 12px;
  color: #94a3b8;
  font-size: 13px;
  pointer-events: none;
}
.input-wrap input {
  width: 100%;
  padding: 8px 12px 8px 24px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #fff;
  color: #0f172a;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.input-wrap input:focus {
  border-color: #1B2A47;
  box-shadow: 0 0 0 4px rgba(27, 42, 71, 0.08);
}

.select-wrap { position: relative; display: flex; align-items: center; }
.select-wrap select {
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
  padding: 8px 32px 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #fff;
  color: #0f172a;
  font-family: inherit;
  outline: none;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.select-wrap select:focus {
  border-color: #1B2A47;
  box-shadow: 0 0 0 4px rgba(27, 42, 71, 0.08);
}
.select-chevron {
  position: absolute;
  right: 10px;
  width: 14px; height: 14px;
  color: #94a3b8;
  pointer-events: none;
}

.date-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #fff;
  color: #0f172a;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.date-input:focus {
  border-color: #1B2A47;
  box-shadow: 0 0 0 4px rgba(27, 42, 71, 0.08);
}

.field-error {
  font-size: 12px;
  color: #dc2626;
  margin: 4px 0 0;
  font-weight: 500;
}

.advanced-foot {
  display: flex;
  justify-content: flex-end;
  padding-top: 4px;
  border-top: 1px solid #f1ede2;
}

.collapse-enter-active, .collapse-leave-active { transition: opacity 0.2s ease, max-height 0.28s ease; overflow: hidden; }
.collapse-enter-from, .collapse-leave-to { opacity: 0; max-height: 0; }
.collapse-enter-to, .collapse-leave-from { opacity: 1; max-height: 600px; }

/* ==================== Loading + empty ==================== */
.po-loading {
  padding: 60px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  flex: 1;
  justify-content: center;
}
.loading-spinner {
  width: 40px; height: 40px;
  border-radius: 999px;
  border: 3px solid #e2e8f0;
  border-top-color: #1B2A47;
  animation: spin 0.9s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.loading-label { font-size: 13px; color: #64748b; }

.loading-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 40px 20px;
}

.empty-hero {
  background: #fff;
  border: 1px dashed #cbd5e1;
  border-radius: 16px;
  padding: 60px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
  flex: 1;
  justify-content: center;
}
.empty-hero-icon {
  width: 56px; height: 56px;
  border-radius: 14px;
  background: #eef2f7;
  color: #1B2A47;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 4px;
}
.empty-hero-icon svg { width: 28px; height: 28px; }
.empty-hero-title { font-size: 15px; font-weight: 700; color: #1b2a47; }
.empty-hero-sub { font-size: 13px; color: #64748b; }

/* ==================== Body ==================== */
.po-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1;
  min-height: 0;
}
.po-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}
.pagination-wrap {
  margin-top: auto;
  padding-top: 8px;
}

/* ==================== Toast ==================== */
.po-toast {
  position: fixed;
  top: 24px; right: 24px;
  z-index: 60;
  background: #1b2a47; color: #fff;
  padding: 12px 18px;
  border-radius: 12px;
  font-size: 13px; font-weight: 500;
  display: inline-flex; align-items: center; gap: 10px;
  box-shadow: 0 12px 30px -10px rgba(27, 42, 71, 0.4);
}
.po-toast .dot { width: 8px; height: 8px; border-radius: 999px; background: #94a3b8; }
.po-toast.is-success { background: #059669; }
.po-toast.is-success .dot { background: #a7f3d0; }
.po-toast.is-error { background: #dc2626; }
.po-toast.is-error .dot { background: #fecaca; }

.toast-enter-active, .toast-leave-active { transition: all 0.24s cubic-bezier(0.4, 0, 0.2, 1); }
.toast-enter-from, .toast-leave-to { transform: translateY(-12px); opacity: 0; }

/* ==================== Modals ==================== */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.modal-card {
  background: #fff;
  border-radius: 18px;
  width: 100%;
  max-width: 480px;
  max-height: calc(100vh - 40px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 60px -20px rgba(15, 23, 42, 0.35);
  border: 1px solid #ede9df;
}
.modal-card-wide { max-width: 640px; }

.modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 22px 14px;
  border-bottom: 1px solid #f1ede2;
}
.modal-eyebrow {
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8a8371;
  font-weight: 700;
}
.modal-title {
  font-size: 18px;
  font-weight: 700;
  color: #1b2a47;
  margin: 2px 0 4px;
  letter-spacing: -0.01em;
}
.modal-sub { font-size: 12.5px; color: #64748b; line-height: 1.4; }
.modal-close {
  border: none;
  background: transparent;
  cursor: pointer;
  color: #94a3b8;
  width: 30px; height: 30px;
  border-radius: 8px;
  display: inline-flex; align-items: center; justify-content: center;
  transition: all 0.12s;
  flex-shrink: 0;
}
.modal-close:hover { background: #f1f5f9; color: #1b2a47; }
.modal-close svg { width: 16px; height: 16px; }
.modal-body {
  padding: 18px 22px;
  overflow-y: auto;
  max-height: calc(100vh - 220px);
}
.modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 22px 18px;
  border-top: 1px solid #f1ede2;
}

/* Create-payout form grid */
.cpo-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.field-full { grid-column: 1 / -1; }
.input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #fbfaf7;
  color: #0f172a;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.input.has-prefix { padding-left: 26px; }
.input.mono { font-family: "SFMono-Regular", Menlo, monospace; letter-spacing: 0.02em; }
.input:focus {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(27, 42, 71, 0.08);
}
.field-invalid .input {
  border-color: #fca5a5;
  background: #fef2f2;
}
@media (max-width: 640px) {
  .cpo-grid { grid-template-columns: 1fr; }
}

.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.22s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }
.modal-fade-enter-active .modal-card, .modal-fade-leave-active .modal-card {
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}
.modal-fade-enter-from .modal-card { transform: translateY(-8px) scale(0.98); }
</style>
